/**
 * Consent-gated Intercom Messenger loader.
 *
 * Logged-in app pages set `window.storyworthIntercomSettings` (via
 * `templates/includes/head-analytics.html`) before this module runs.
 * OneTrust classifies Intercom's `intercom-*` cookies as Functional (C0003),
 * so the Messenger is only booted while that group is active: the widget
 * script loads on the first grant, and on withdrawal the Messenger is shut
 * down. Whenever OneTrust reports the group as denied, `intercom-*` cookies
 * are cleared, including ones left by an earlier visit. A later grant on the
 * same page boots it again. Reference:
 * https://developers.intercom.com/installing-intercom/web/methods
 */
import { cookieString, clearStoryworthCookie } from "./cookies"
import { hasFunctionalConsent, oneTrustHasReported } from "./consent"
import { type IntercomFunction } from "./types"

const INTERCOM_COOKIE_PREFIX = "intercom-"

let intercomScriptLoaded = false
let intercomBooted = false
let intercomStub: IntercomFunction | undefined

export function initializeIntercomConsentSync() {
  if (!window.storyworthIntercomSettings?.app_id) return

  syncIntercomConsent()
  window.addEventListener("OneTrustGroupsUpdated", syncIntercomConsent)
}

function syncIntercomConsent() {
  const settings = window.storyworthIntercomSettings
  if (!settings?.app_id) return

  if (hasFunctionalConsent()) {
    if (intercomBooted) return

    intercomBooted = true
    installIntercomStub()
    window.Intercom?.("boot", settings)
    if (!intercomScriptLoaded) {
      intercomScriptLoaded = true
      loadIntercomScript(settings.app_id)
    }
  } else if (oneTrustHasReported()) {
    // Clearing waits for OneTrust's report: before it, hasFunctionalConsent()
    // is false for consented visitors too, and clearing then would reset
    // their Intercom device and session on every page load.
    if (intercomBooted) {
      intercomBooted = false
      shutDownIntercom()
    }
    clearIntercomCookies()
  }
}

/**
 * Until the widget script replaces the stub, the boot is still queued and
 * would replay on load ahead of any queued `shutdown`, so it is dropped from
 * the queue instead. Once the widget is live, `shutdown` ends its session.
 */
function shutDownIntercom() {
  if (intercomStub && window.Intercom === intercomStub) {
    intercomStub.q = []
  } else {
    window.Intercom?.("shutdown")
  }
}

/**
 * Installs the standard command queue stub so `Intercom(...)` calls made
 * before the widget script finishes loading are replayed once it does.
 */
function installIntercomStub() {
  if (window.Intercom) return

  const stub: IntercomFunction = (...args: unknown[]) => {
    stub.q = stub.q || []
    stub.q.push(args)
  }
  intercomStub = stub
  window.Intercom = stub
}

/**
 * If consent was withdrawn while the script downloaded, the widget may still
 * have set cookies while initializing, so the withdrawal is re-applied once
 * it has loaded.
 */
function loadIntercomScript(appId: string) {
  const script = document.createElement("script")
  script.async = true
  script.src = `https://widget.intercom.io/widget/${encodeURIComponent(appId)}`
  script.addEventListener("load", () => {
    if (intercomBooted) return

    window.Intercom?.("shutdown")
    clearIntercomCookies()
  })
  document.head.appendChild(script)
}

/**
 * `shutdown` ends the Messenger session, but Intercom keeps its device id
 * cookie; every `intercom-*` cookie is expired both host-only and on
 * `.storyworth.com`, since Intercom may have set it on either.
 */
function clearIntercomCookies() {
  const names = document.cookie
    .split("; ")
    .map((cookie) => cookie.split("=")[0])
    .filter((name) => name.startsWith(INTERCOM_COOKIE_PREFIX))

  for (const name of names) {
    document.cookie = cookieString(name, "", ["max-age=0", "path=/"])
    clearStoryworthCookie(name)
  }
}

export function resetIntercomForTests() {
  intercomScriptLoaded = false
  intercomBooted = false
  intercomStub = undefined
  window.removeEventListener("OneTrustGroupsUpdated", syncIntercomConsent)
}
